import React, { useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Tabs, Select } from 'antd';
import { Layout } from 'features/layout';
import CampaignPerformanceContainer from 'features/performance/containers/CampaignPerformanceContainer';
import { PerformanceFilterInput } from 'features/performance/types/performance.types';
import { CampaignTableContainer, CampaignFormContainer } from 'features/campaigns';
import { CampaignFilterInput } from 'features/campaigns/types';
import { ListedLeadsTableContainer, ListedLeadsFormContainer } from 'features/listed-leads';
import { ListedLeadFilterInput } from 'features/listed-leads/types/listedLeads.types';
import { ListedLeadsLogTableContainer } from 'features/listed-leads-log/containers';
import { ListedLeadsLogFilterInput } from 'features/listed-leads-log/types/listedLeadsLog.types';
import { CallLogsTableContainer } from 'features/callLogs/containers';
import { CallLogsFilterInput } from 'features/callLogs/types/callLogs.types';
import Filter from 'img/ArrowsDownUp.svg';
import { ToastContainer } from 'react-toastify';
import OfficeNames from 'components/officeNames';

const CampaignOverview: React.FC = () => {
  const userDetails = useSelector((state: any) => state.user?.userData);
  const startDate = useSelector((state: any) => state.dropDown?.startDate);
  const endDate = useSelector((state: any) => state.dropDown?.endDate);
  const selectedOfficeId = useSelector((state: any) => state.dropDown?.officeId);
  const [activeTab, setActiveTab] = useState('overview');
  const [filterBy, setFilterBy] = useState<'all' | 'office'>('all');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Role-based access control for filters
  const userRole = userDetails?.role;
  const isRootUser = userRole && ['root_user', 'root_custo'].includes(userRole);
  const hasDownlineAccess = userDetails?.userAccess?.downlineAccess === true;
  const canSelectOffice = isRootUser || hasDownlineAccess;
  const canViewAllOffices = isRootUser; // Only root users can see "All Offices"

  // Use user's office if they can't select offices
  const effectiveOfficeId = canSelectOffice ? selectedOfficeId : userDetails?.officeId;
  // Non-root users with downline access default to 'office' filter
  const effectiveFilterBy = !canSelectOffice ? 'office' : (hasDownlineAccess && !isRootUser ? 'office' : filterBy);

  // Build filter based on user role
  const getFilter = (): PerformanceFilterInput => {
    const filter: PerformanceFilterInput = {};

    if (startDate) filter.startDate = startDate;
    if (endDate) filter.endDate = endDate;

    // Non-root users must always be filtered by their office
    if (!isRootUser) {
      // Use effective office (selected office for downline access, or user's office)
      if (effectiveOfficeId) {
        filter.officeId = effectiveOfficeId;
      } else if (userDetails?.officeId) {
        filter.officeId = userDetails.officeId;
      }
    } else if (effectiveFilterBy === 'office' && effectiveOfficeId) {
      // Root users can choose to filter by office
      filter.officeId = effectiveOfficeId;
    }

    return filter;
  };

  const getCampaignFilter = useCallback((): CampaignFilterInput => {
    const filter: CampaignFilterInput = {};

    // Add officeId based on effective filter
    if (effectiveFilterBy === 'office' && effectiveOfficeId) {
      filter.officeId = effectiveOfficeId;
    }

    return filter;
  }, [effectiveFilterBy, effectiveOfficeId]);

  const getListedLeadFilter = useCallback((): ListedLeadFilterInput => {
    const filter: ListedLeadFilterInput = {};

    // Add officeId based on effective filter
    if (effectiveFilterBy === 'office' && effectiveOfficeId) {
      filter.officeId = effectiveOfficeId;
    }

    return filter;
  }, [effectiveFilterBy, effectiveOfficeId]);

  const getListedLeadLogFilter = useCallback((): ListedLeadsLogFilterInput => {
    const filter: ListedLeadsLogFilterInput = {};

    if (startDate) filter.startDate = startDate;
    if (endDate) filter.endDate = endDate;

    // Add officeId based on effective filter
    if (effectiveFilterBy === 'office' && effectiveOfficeId) {
      filter.officeId = effectiveOfficeId;
    }

    return filter;
  }, [effectiveFilterBy, effectiveOfficeId, startDate, endDate]);

  const getCallLogsFilter = useCallback((): CallLogsFilterInput => {
    const filter: CallLogsFilterInput = {};

    if (startDate) filter.startDate = startDate;
    if (endDate) filter.endDate = endDate;

    // Add officeId based on effective filter
    if (effectiveFilterBy === 'office' && effectiveOfficeId) {
      filter.officeId = effectiveOfficeId;
    }

    return filter;
  }, [effectiveFilterBy, effectiveOfficeId, startDate, endDate]);

  const filter = getFilter();
  const campaignFilter = useMemo(() => getCampaignFilter(), [getCampaignFilter]);
  const listedLeadFilter = useMemo(() => getListedLeadFilter(), [getListedLeadFilter]);
  const listedLeadLogFilter = useMemo(() => getListedLeadLogFilter(), [getListedLeadLogFilter]);
  const callLogsFilter = useMemo(() => getCallLogsFilter(), [getCallLogsFilter]);

  const items = [
    {
      key: 'overview',
      label: 'Campaign Performance',
      children: (
        <div style={{ marginTop: '20px' }}>
          <CampaignPerformanceContainer filter={filter} />
        </div>
      ),
    },
    // Only show Campaigns tab for non-root users
    ...(!isRootUser ? [
      {
        key: 'campaigns',
        label: 'Campaigns',
        children: (
          <div style={{ marginTop: '20px' }}>
            <CampaignTableContainer filter={campaignFilter} key={refreshTrigger} />
          </div>
        ),
      },
      {
        key: 'listed-leads',
        label: 'Listed Leads',
        children: (
          <div style={{ marginTop: '20px' }}>
            <ListedLeadsTableContainer filter={listedLeadFilter} key={refreshTrigger} />
          </div>
        ),
      },
      {
        key: 'listed-leads-logs',
        label: 'Listed Leads Logs',
        children: (
          <div style={{ marginTop: '20px' }}>
            <ListedLeadsLogTableContainer filter={listedLeadLogFilter} />
          </div>
        ),
      },
    ] : []),
    {
      key: 'call-logs',
      label: 'Call Logs',
      children: (
        <div style={{ marginTop: '20px' }}>
          <CallLogsTableContainer filter={callLogsFilter} />
        </div>
      ),
    },
  ];

  return (
    <Layout>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <div className='campaign-header-container' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1>Campaign Overview</h1>
          {isRootUser && activeTab === 'campaigns' && (
            <CampaignFormContainer onSuccess={() => {
              setRefreshTrigger(prev => prev + 1);
            }} />
          )}
          {isRootUser && activeTab === 'listed-leads' && (
            <ListedLeadsFormContainer onSuccess={() => {
              setRefreshTrigger(prev => prev + 1);
            }} />
          )}
        </div>

        <div className='filter-container'>
          <div className='filter'>
            <img src={Filter} alt='Filter' />
            <span>Filter By</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginLeft: '20px' }}>
              {canViewAllOffices && (
                <Select
                  value={filterBy}
                  onChange={(value) => setFilterBy(value)}
                  style={{ width: 150 }}
                >
                  <Select.Option value='all'>All Offices</Select.Option>
                  <Select.Option value='office'>Office</Select.Option>
                </Select>
              )}
              {canSelectOffice && (
                <div style={{ minWidth: '200px' }}>
                  <OfficeNames />
                </div>
              )}
            </div>
          </div>
        </div>

        <Tabs
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key)}
          items={items}
        />

        <ToastContainer />
      </div>
    </Layout>
  );
};

export default CampaignOverview;
